import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowUp, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';
import { publicApi } from '@/lib/api';

const Footer = () => {
  const [settings, setSettings] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    publicApi.getSiteSettings().then(res => setSettings(res.data)).catch(() => {});
    publicApi.getRecentPosts(3).then(res => setRecentPosts(res.data)).catch(() => {});

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const usefulLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Free Resources', path: '/free-resources' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact Us', path: '/contact-us' },
  ];

  return (
    <footer className="bg-slate-900 text-slate-300">
      {/* Careersupport.io Banner */}
      <div className="bg-accent py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <h3 className="text-white font-heading font-bold text-xl">All-in-One Career Advancement Platform</h3>
              <p className="text-white/90 text-sm">Designed to Help Job Seekers - Get a FREE account on Careersupport.io</p>
            </div>
            <a
              href="https://careersupport.io"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-accent font-bold rounded-lg hover:bg-white/90 transition-colors"
              data-testid="footer-careersupport-cta"
            >
              Sign Up Free
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* About Column */}
          <div>
            <h3 className="text-white font-heading font-bold text-xl mb-4">About Us</h3>
            <p className="text-sm leading-relaxed mb-4">
              {settings?.about_text || 'SEG Career Support helps individuals to discover their career aligned purpose by assisting with all areas of Career development.'}
            </p>
            <div className="flex gap-3">
              {settings?.facebook_url && (
                <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 rounded-full hover:bg-primary transition-colors" data-testid="footer-facebook">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {settings?.twitter_url && (
                <a href={settings.twitter_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 rounded-full hover:bg-primary transition-colors" data-testid="footer-twitter">
                  <Twitter className="w-4 h-4" />
                </a>
              )}
              {settings?.linkedin_url && (
                <a href={settings.linkedin_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 rounded-full hover:bg-primary transition-colors" data-testid="footer-linkedin">
                  <Linkedin className="w-4 h-4" />
                </a>
              )}
              {settings?.instagram_url && (
                <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-800 rounded-full hover:bg-primary transition-colors" data-testid="footer-instagram">
                  <Instagram className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* Useful Links */}
          <div>
            <h3 className="text-white font-heading font-bold text-xl mb-4">Useful Links</h3>
            <ul className="space-y-2">
              {usefulLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm hover:text-primary transition-colors flex items-center gap-2"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Recent Posts */}
          <div>
            <h3 className="text-white font-heading font-bold text-xl mb-4">Recent Posts</h3>
            <ul className="space-y-4">
              {recentPosts.map((post) => (
                <li key={post.id}>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="text-sm hover:text-primary transition-colors block"
                    data-testid={`footer-post-${post.slug}`}
                  >
                    {post.title}
                  </Link>
                  <span className="text-xs text-slate-500">
                    {new Date(post.created_at).toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-heading font-bold text-xl mb-4">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-white mb-1">Mailing Address</p>
                  <p>SEG Career Support LLC</p>
                  <p>6205 Abercorn Street, STE 206</p>
                  <p>Savannah, Georgia 31405</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                <a href={`tel:${settings?.phone}`} className="text-sm hover:text-primary transition-colors">
                  {settings?.phone || '(912) 376-9006'}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary flex-shrink-0" />
                <a href={`mailto:${settings?.email}`} className="text-sm hover:text-primary transition-colors">
                  {settings?.email || 'info@segcareersupport.com'}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            Copyright © {settings?.copyright_text || '2024 SEG Career Support. All Rights Reserved.'}
          </p>
          <Link to="/admin/login" className="text-xs text-slate-600 hover:text-slate-400 transition-colors">
            Admin
          </Link>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-all z-50"
          data-testid="scroll-to-top-btn"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </footer>
  );
};

export default Footer;
